import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiUser, FiMail, FiPhone, FiMapPin, FiEdit2, 
  FiSave, FiX, FiBook, FiClock, FiCheckCircle,
  FiCamera, FiLock, FiUpload
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import LogoutModal from '../../components/LogoutModal/LogoutModal';
import './Profil.css';

const Profil = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [stats, setStats] = useState({
    totalBorrowed: 0,
    activeBorrowings: 0,
    returned: 0
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setLoggingOut(true);
    setTimeout(() => {
      logout();
      navigate('/login');
    }, 500);
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [profileRes, statsRes] = await Promise.all([
          api.getProfile(user?.id),
          api.getUserStats(user?.id)
        ]);

        if (profileRes.success) {
          setProfile(profileRes.data);
          setFormData(profileRes.data);
        }

        if (statsRes.success) {
          setStats(statsRes.data);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.updateProfile(user?.id, formData);
      if (response.success) {
        setProfile(formData);
        setEditing(false);
        setMessage({ type: 'success', text: 'Profil berhasil diperbarui!' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Gagal menyimpan profil' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Terjadi kesalahan. Silakan coba lagi.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(profile);
    setEditing(false);
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Format file tidak didukung. Gunakan JPEG, PNG, GIF, atau WebP.' });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Ukuran file maksimal 2MB.' });
      return;
    }

    setUploadingPhoto(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.uploadProfilePhoto(user?.id, file);
      if (response.success) {
        const newFoto = response.data.foto;
        setProfile(prev => ({ ...prev, foto: newFoto }));
        // Update user context so Header and Sidebar also update
        updateUser({ ...user, foto: newFoto });
        setMessage({ type: 'success', text: 'Foto profil berhasil diupdate!' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Gagal upload foto.' });
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      setMessage({ type: 'error', text: 'Terjadi kesalahan saat upload foto.' });
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Password baru tidak cocok!' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password minimal 6 karakter!' });
      return;
    }

    // API call for password change would go here
    setMessage({ type: 'success', text: 'Password berhasil diubah!' });
    setShowPasswordModal(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  // Use profile data for borrow limits, fallback to defaults
  const borrowLimit = profile?.max_pinjam || (user?.role === 'dosen' ? 5 : 3);
  const borrowDays = profile?.lama_pinjam || (user?.role === 'dosen' ? 14 : 7);

  if (loading) {
    return (
      <div className="profil__loading">
        <div className="profil__loading-spinner"></div>
        <p>Memuat profil...</p>
      </div>
    );
  }

  return (
    <div className="profil">
      {/* Message */}
      {message.text && (
        <div className={`profil__message profil__message--${message.type}`}>
          {message.type === 'success' ? <FiCheckCircle /> : <FiX />}
          <span>{message.text}</span>
          <button onClick={() => setMessage({ type: '', text: '' })}>
            <FiX />
          </button>
        </div>
      )}

      <div className="profil__grid">
        {/* Profile Card */}
        <div className="profil__card profil__card--main">
          <div className="profil__avatar-section">
            <div className="profil__avatar">
              {profile?.foto ? (
                <img src={`${profile.foto}`} alt={profile.nama} />
              ) : (
                <span>{profile?.nama?.charAt(0).toUpperCase() || 'U'}</span>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoChange}
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
              />
              <button 
                className="profil__avatar-edit" 
                onClick={handlePhotoClick}
                disabled={uploadingPhoto}
                title="Ganti foto profil"
              >
                {uploadingPhoto ? <FiUpload className="spinning" /> : <FiCamera />}
              </button>
            </div>
            <div className="profil__avatar-info">
              <h2>{profile?.nama || 'User'}</h2>
              <span className="profil__role">
                {user?.role === 'dosen' ? '👨‍🏫 Dosen' : '🎓 Mahasiswa'}
              </span>
              <span className="profil__id">
                {user?.role === 'dosen' 
                  ? `NIDN: ${profile?.nidn || '-'}${profile?.nuptk ? ` | NUPTK: ${profile.nuptk}` : ''}`
                  : `NIM: ${profile?.nim || '-'}`
                }
              </span>
            </div>
          </div>

          <div className="profil__privileges">
            <h4>Hak Peminjaman</h4>
            <div className="profil__privileges-grid">
              <div className="profil__privilege">
                <FiBook />
                <div>
                  <span className="value">{borrowLimit} Buku</span>
                  <span className="label">Maksimal Pinjam</span>
                </div>
              </div>
              <div className="profil__privilege">
                <FiClock />
                <div>
                  <span className="value">{borrowDays} Hari</span>
                  <span className="label">Durasi Pinjam</span>
                </div>
              </div>
            </div>
          </div>

          <div className="profil__stats">
            <div className="profil__stat">
              <span className="profil__stat-value">{stats.totalBorrowed || 0}</span>
              <span className="profil__stat-label">Total Pinjaman</span>
            </div>
            <div className="profil__stat">
              <span className="profil__stat-value">{stats.activeBorrowings || 0}</span>
              <span className="profil__stat-label">Sedang Dipinjam</span>
            </div>
            <div className="profil__stat">
              <span className="profil__stat-value">{stats.returned || 0}</span>
              <span className="profil__stat-label">Dikembalikan</span>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="profil__card profil__card--details">
          <div className="profil__card-header">
            <h3>Informasi Pribadi</h3>
            {!editing ? (
              <button className="profil__edit-btn" onClick={() => setEditing(true)}>
                <FiEdit2 /> Edit
              </button>
            ) : (
              <div className="profil__edit-actions">
                <button className="profil__cancel-btn" onClick={handleCancel}>
                  <FiX /> Batal
                </button>
                <button 
                  className="profil__save-btn" 
                  onClick={handleSave}
                  disabled={saving}
                >
                  <FiSave /> {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            )}
          </div>

          <div className="profil__form">
            <div className="profil__field">
              <label>
                <FiUser /> Nama Lengkap
              </label>
              {editing ? (
                <input
                  type="text"
                  name="nama"
                  value={formData.nama || ''}
                  onChange={handleChange}
                />
              ) : (
                <p>{profile?.nama || '-'}</p>
              )}
            </div>

            <div className="profil__field">
              <label>
                <FiMail /> Email
              </label>
              <p>{profile?.email || '-'}</p>
              <span className="profil__field-note">Email tidak dapat diubah</span>
            </div>

            <div className="profil__field">
              <label>
                <FiPhone /> No. Telepon
              </label>
              {editing ? (
                <input
                  type="tel"
                  name="telepon"
                  value={formData.telepon || ''}
                  onChange={handleChange}
                  placeholder="08xxxxxxxxxx"
                />
              ) : (
                <p>{profile?.telepon || '-'}</p>
              )}
            </div>

            <div className="profil__field">
              <label>
                <FiMapPin /> Alamat
              </label>
              {editing ? (
                <textarea
                  name="alamat"
                  value={formData.alamat || ''}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Masukkan alamat lengkap"
                />
              ) : (
                <p>{profile?.alamat || '-'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Account Settings */}
        <div className="profil__card profil__card--settings">
          <h3>Pengaturan Akun</h3>
          
          <div className="profil__settings-list">
            <button 
              className="profil__setting-item"
              onClick={() => setShowPasswordModal(true)}
            >
              <div className="profil__setting-icon">
                <FiLock />
              </div>
              <div className="profil__setting-info">
                <span className="profil__setting-title">Ubah Password</span>
                <span className="profil__setting-desc">Perbarui password akun Anda</span>
              </div>
            </button>

            <button 
              className="profil__setting-item profil__setting-item--danger"
              onClick={handleLogoutClick}
            >
              <div className="profil__setting-icon">
                <FiX />
              </div>
              <div className="profil__setting-info">
                <span className="profil__setting-title">Keluar</span>
                <span className="profil__setting-desc">Logout dari akun ini</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal 
        isOpen={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        loading={loggingOut}
      />

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="profil__modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="profil__modal" onClick={(e) => e.stopPropagation()}>
            <div className="profil__modal-header">
              <h3>Ubah Password</h3>
              <button onClick={() => setShowPasswordModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="profil__modal-body">
              <div className="profil__field">
                <label>Password Saat Ini</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Masukkan password saat ini"
                />
              </div>
              <div className="profil__field">
                <label>Password Baru</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Masukkan password baru"
                />
              </div>
              <div className="profil__field">
                <label>Konfirmasi Password Baru</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Ulangi password baru"
                />
              </div>
            </div>
            <div className="profil__modal-actions">
              <button className="cancel-btn" onClick={() => setShowPasswordModal(false)}>
                Batal
              </button>
              <button className="confirm-btn" onClick={handlePasswordChange}>
                Simpan Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profil;
